import type { TFunction } from 'i18next';
import type { MatchType } from '@draughts/shared';

/**
 * Etichette tradotte delle opzioni di partita — colore e modalità di gioco.
 *
 * Stesso motivo per cui esiste `challenge-icons.tsx`: le stesse opzioni compaiono
 * ormai in due modali (Sfida diretta fra umani e Sfida AI), e una coppia di funzioni
 * copiate diverge alla prima aggiunta di una modalità. Le chiavi vivono sotto
 * `online.challenge.*` per ragioni storiche, ma le stringhe sono generiche.
 *
 * L'apertura ha già il suo helper condiviso, `opening-labels.ts`, perché deve anche
 * calcolare la dimensione del pool a partire dalla variante.
 */

/** Bianco / Nero / Casuale. */
export const colorLabel = (t: TFunction, color: string): string => {
  if (color === 'white') return t('game.clock.white');
  if (color === 'black') return t('game.clock.black');
  return t('online.challenge.colorRandom');
};

/** Normale / Breakthrough / Antidraughts. */
export const gameModeLabel = (t: TFunction, mode: string): string => {
  if (mode === 'breakthrough') return t('online.challenge.modeBreakthrough');
  if (mode === 'antidraughts') return t('online.challenge.modeAntidraughts');
  if (mode === 'blind') return t('online.challenge.modeBlind');
  return t('online.challenge.modeNormal');
};

/**
 * Qualsiasi / Solo valida / Solo amichevole.
 *
 * Takes the type and the legacy boolean, because both still arrive: a challenge
 * or a table created before this shipped carries only `rated`, where `true`
 * always meant "counts if it can" — which is exactly `'any'`.
 */
export const matchTypeLabel = (t: TFunction, matchType?: string, rated?: boolean): string => {
  const resolved = matchType ?? (rated === false ? 'casual' : 'any');
  if (resolved === 'rated') return t('online.challenge.rated');
  if (resolved === 'casual') return t('online.challenge.casual');
  return t('online.challenge.gameTypeAny');
};

/** The value `gameTypeLeading` needs for the same pair of inputs. */
export const matchTypeValue = (matchType?: string, rated?: boolean): MatchType =>
  (matchType as MatchType | undefined) ?? (rated === false ? 'casual' : 'any');

/**
 * Il tipo partita detto a chi ha l'avversario gia' davanti.
 *
 * Due differenze da `matchTypeLabel`, e vengono dallo stesso fatto: qui la porta e'
 * gia' chiusa su una persona sola. Niente «Valutata/Amichevole» — con QUESTO
 * avversario la partita conta o non conta, e si sa quale delle due. E «Valutata» non
 * ha bisogno del «(senza ospiti)»: quello e' il cartello sulla porta, non il nome del
 * tipo di partita, e qui non c'e' nessuno da tenere fuori.
 *
 * Chiavi `game.*` e non `online.challenge.*`: sono le stesse parole della pastiglia
 * in partita, gia' tradotte in tutte e otto le lingue — e questa riga promette
 * esattamente cio' che quella pastiglia confermera'. Stesso motivo per cui
 * `colorLabel` qui sopra chiede `game.clock.white`.
 */
export const knownMatchTypeLabel = (t: TFunction, matchType: Exclude<MatchType, 'any'>): string =>
  matchType === 'rated' ? t('game.header.rated') : t('game.header.casual');
